import React, { useEffect, useState, Suspense } from "react";
import styles from "../../styles/SchoolEnquiry/enquiry-form.module.css";
import ReactSelect from "../ReactSelect";
import { isNonEmpty, isValidEmail, isValidMobile, } from "../../utils/common-utils";
import { saveSchoolEnquiry, SendOTPForSchoolEnquiry, VerifyForSchoolEnquiry } from "../../apis/auth";
import { showToast } from "../CustomToast";
import { trackSchoolEnquiryConversion } from "../../utils/RouteConstants";

const MdOutlineClose = React.lazy(() => import('react-icons/md').then(module => ({ default: module.MdOutlineClose })));

const OtpText = `OTP will be sent to your mobile number for verification`;
const designationOptions = [
    { value: "School Leadership", label: "School Leadership" },
    { value: "Principal", label: "Principal" },
    { value: "Vice Principal", label: "Vice Principal" },
    { value: "Administrator", label: "Administrator" },
    { value: "Teacher", label: "Teacher" },
    { value: "Student", label: "Student" },
    { value: "Other", label: "Other" }
];

const challengeOptions = [
    "Improving student performance",
    "Creating personalised homework in seconds",
    "Unlocking specific student insights",
    "Making parents more engaged",
    "Building student portfolio and learner profile"
];

export default function EnquiryForm({ onClose, handleUpdateStep,otpRequired = true }) {
    const [formData, setFormData] = useState({ challenges: "Unlocking specific student insights" });
    const [otp, setOtp] = useState("");
    const [seconds, setSeconds] = useState(180);
    const [authData, setAuthData] = useState({});
    const [step, setStep] = useState(0);
    const [isTrigger, setIsTrigger] = useState(false);
    const [showOtherDesignation, setShowOtherDesignation] = useState(false);

    useEffect(() => {
        setSeconds(0);
        setStep(0);
        setIsTrigger(false);
    }, []);

    useEffect(() => {
        let interval = null;
        if (seconds > 0) {
            interval = setInterval(() => {
                setSeconds((prevSeconds) => prevSeconds - 1);
            }, 1000);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [seconds]);


    const handleChange = (key, value) => {
        if (key === "phoneNumber") {
            value = value?.replace(/[^0-9]/g, '');
            value = value?.slice(0, 10);
        }
        if (key === "designation" && value === "Other") {
            setShowOtherDesignation(true);
            // Reset otherDesignation when switching to "Other"
            setFormData((prev) => ({ ...prev, [key]: value, otherDesignation: "" }));
        } else if (key === "designation") {
            setShowOtherDesignation(false);
            setFormData((prev) => ({ ...prev, [key]: value, otherDesignation: null }));
        } else {
            setFormData((prev) => ({ ...prev, [key]: value }));
        }
    };

    const handleDesignationChange = (selectedOption) => {
        const value = selectedOption ? selectedOption.value : "";
        handleChange("designation", value);
    };

    // Double-check validation in submit function
    const handleSendOTP = async () => {
        // Special validation for Other designation
        if (formData?.designation === "Other" && !isNonEmpty(formData?.otherDesignation)) {
            showToast("Please specify your designation", "error");
            return;
        }

        // Regular flow continues
        setIsTrigger(true);
        const { success, data } = await SendOTPForSchoolEnquiry(
            formData?.phoneNumber,
        );
        setIsTrigger(false);
        if (success) {
            setSeconds(180);
            setAuthData(data?.data);
            setStep(1);
            showToast("OTP sent successfully!", "success");
        } else {
            showToast(data?.message || "Failed to send OTP", "error");
        }
    };

    const handleVerifyOTP = async () => {
        setIsTrigger(true);
        const { success, message } = await VerifyForSchoolEnquiry(authData, otp);
        if (success) {
            // Prepare body with UTM parameters
            let body = { ...formData };
            try {
                const utmSource = localStorage.getItem("utm_source");
                if (utmSource && utmSource.trim() !== "") {
                    body["utmSource"] = utmSource;
                } else {
                    const externalRef = localStorage.getItem("externalRef");
                    body["utmSource"] = externalRef;
                }
            } catch (e) {
            }

            saveSchoolEnquiry(body);
            // Send Enhanced Conversion to Google Ads
            trackSchoolEnquiryConversion(body);

            setIsTrigger(false);
            
            // Jump directly to confirmation modal
            handleUpdateStep(1);
        } else {
            setIsTrigger(false);
            showToast(message || "Invalid OTP", "error");
        }
    };

    const resend = () => (
        <div style={{ marginTop: "8px" }}>
            <div className={styles.resendTextContainer}>
                {seconds > 0 && (
                    <div>
                        <b style={{ color: "#130159", textDecoration: "underline" }}>
                            {seconds > 0 && `Resend `}
                        </b>
                        <span style={{ color: "#130159", marginRight: "4px" }}>
                            {seconds > 0 && formatTime(seconds)}
                        </span>
                    </div>
                )}

                {seconds === 0 && (
                    <div
                        style={{ color: "#130159", cursor: "pointer" }}
                        onClick={() => handleSendOTP()}
                    >
                        Resend
                    </div>
                )}
            </div>
        </div>
    );

    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    const handleBtnClick = () => {

    if (!otpRequired) {
        handleDirectSubmit();
        return;
    }

    if (step === 0) {
        handleSendOTP();
    } else if (isNonEmpty(otp)) {
        handleVerifyOTP();
    }
};

    const handleDirectSubmit = async () => {
        setIsTrigger(true);

        let body = { ...formData };

        try {
            const utmSource = localStorage.getItem("utm_source");
            if (utmSource && utmSource.trim() !== "") {
                body["utmSource"] = utmSource;
            } else {
                const externalRef = localStorage.getItem("externalRef");
                body["utmSource"] = externalRef;
            }
        } catch (e) {
        }

        await saveSchoolEnquiry(body);
        trackSchoolEnquiryConversion(body);

        setIsTrigger(false);

        handleUpdateStep(1);
    };


    const handleOTPChange = (_, input) => {
        input = input?.replace(/[^0-9]/g, '');
        input = input?.slice(0, 4);
        setOtp(input)
    }

    let canSubmit =
        isNonEmpty(formData?.name) &&
        isValidMobile(formData?.phoneNumber) &&
        isValidEmail(formData?.email) &&
        isNonEmpty(formData?.city) &&
        isNonEmpty(formData?.schoolName) &&
        isNonEmpty(formData?.designation) &&
        isNonEmpty(formData?.challenges);
    if (formData?.designation == "Other" && !isNonEmpty(formData?.otherDesignation?.trim())) {
        canSubmit = false;
    }
    let isDisabled = true;
    if (step === 0) {
        isDisabled = canSubmit ? false : true;
    } else if (step == 1) {
        isDisabled = isNonEmpty(otp) ? false : true;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.title}>Get a walkthrough of SchoolAi</div>
                <div className={styles.close} onClick={onClose}>
                    <Suspense fallback={<div>Loading...</div>}>
                        <MdOutlineClose color="#5F4DC7" size={24} />
                    </Suspense>
                </div>
            </div>
            {/* Description section explaining the key benefits of SchoolAi platform */}
            <div className={styles.description}>
                {`Coschool's closed-loop learning platform for schools helps teachers personalise learning, students get the right support on time, parents reinforce learning at home, and school leaders act on real-time insights`}
            </div>
            <div>
                <div className={styles.subHeader}>School information</div>
                <div className={styles.inputContainer}>
                    <ReactSelect
                        title={"Designation"}
                        placeholder={"Select Designation"}
                        value={designationOptions.find(option => option.value === formData.designation) || null}
                        options={designationOptions}
                        onChange={handleDesignationChange}
                    />
                    {showOtherDesignation && (
                        <CustomInput
                            title={"Please specify your designation"}
                            placeholder={"Your designation"}
                            keyProp={"otherDesignation"}
                            data={formData}
                            onChange={handleChange}
                            isShow={true}
                        />
                    )}
                    <CustomInput
                        title={"School Name"}
                        placeholder={"School Name"}
                        keyProp={"schoolName"}
                        data={formData}
                        onChange={handleChange}
                    />
                    <CustomInput
                        title={"City"}
                        placeholder={"City"}
                        keyProp={"city"}
                        data={formData}
                        onChange={handleChange}
                    />
                </div>
                <div className={styles.challengeSection}>
                    <div className={styles.challengeTitle}>What are the challenges you'd like to solve for your school?</div>
                    <div className={styles.challengeOptions}>
                        {challengeOptions.map((option) => (
                            <RadioOption
                                key={option}
                                label={option}
                                value={option}
                                selected={formData?.challenges}
                                onChange={(value) => handleChange("challenges", value)}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <div>
                <div className={styles.subHeader}>Personal information</div>
                <div className={styles.inputContainer}>
                    <CustomInput
                        title={"Full Name"}
                        placeholder={"Full Name"}
                        keyProp={"name"}
                        data={formData}
                        onChange={handleChange}
                    />
                    <CustomInput
                        title={"Mobile Number"}
                        placeholder={"+91 12345 67890"}
                        keyProp={"phoneNumber"}
                        data={formData}
                        onChange={handleChange}
                    />
                    <CustomInput
                        title={"Email"}
                        placeholder={"Email"}
                        keyProp={"email"}
                        data={formData}
                        onChange={handleChange}
                    />
                </div>
            </div>
            <div>
               {otpRequired && step === 1 && (
                    <div style={{ margin: "16px 0px" }}>
                        <CustomInput
                            title={"Enter OTP"}
                            placeholder={"Enter OTP"}
                            value={otp}
                            isShow={false}
                            onChange={handleOTPChange}
                        />
                        {resend()}
                    </div>
                )}
               
                {otpRequired && step == 0 && <div className={styles.otpText}>{OtpText}</div>}
                <div
                    className={`${styles.button} ${isDisabled && styles.disabled}`}
                    onClick={() => {
                        if (!isDisabled && !isTrigger) {
                            handleBtnClick();
                        }
                    }}
                >
                    {isTrigger ?
                        <div className={styles.loader} /> :
                        otpRequired
                        ? (step == 0 ? "Get your demo" : "Submit")
                        : "Submit"
                    }
                </div>
            </div>
        </div>
    );
}

const CustomInput = (props) => {
    const { keyProp, data, onChange, value = "", isShow = true } = props;
    return (
        <div>
            <div className={styles.inputLabel}>
                {props?.title}
                {isShow && <span style={{ color: "#212121D9" }}>*</span>}
            </div>
            <input
                className={styles.input}
                placeholder={props?.placeholder}
                value={keyProp ? data[keyProp] : value}
                onChange={(e) => onChange(keyProp, e.target.value)}
            />
        </div>
    );
};

const RadioOption = ({ label, value, selected, onChange }) => {
    return (
        <div className={styles.radioOption} onClick={() => onChange(value)}>
            <div className={`${styles.radioButton} ${selected === value ? styles.selected : ''}`}>
                {selected === value && <div className={styles.radioInner}></div>}
            </div>
            <span className={styles.radioLabel}>{label}</span>
        </div>
    );
};


